import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { isNativeShell } from '../../utils/distribution';
import {
  clearBiometricSessionUnlocked,
  isBiometricUnlockEnabled,
  markBiometricSessionUnlocked,
  shouldShowBiometricUnlockGate,
  verifyBiometricUnlock,
} from '../../utils/biometricUnlock';

/**
 * Full-screen gate for Capacitor shells: resumes an already-issued session after
 * device biometrics. Never authenticates with the API.
 */
const NativeBiometricUnlockOverlay: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [locked, setLocked] = useState(() => shouldShowBiometricUnlockGate());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attemptingRef = useRef(false);

  const reevaluate = useCallback(() => {
    setLocked(shouldShowBiometricUnlockGate());
  }, []);

  const attemptUnlock = useCallback(async () => {
    if (attemptingRef.current) return;
    attemptingRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const ok = await verifyBiometricUnlock();
      if (ok) {
        setLocked(false);
      } else {
        setError('Biometric unlock did not succeed. Try again or use your password.');
      }
    } finally {
      setBusy(false);
      attemptingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isNativeShell() || !isAuthenticated) {
      setLocked(false);
      return undefined;
    }

    reevaluate();

    let removeAppListener: (() => void) | undefined;
    void (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appStateChange', ({ isActive }) => {
          if (!isActive && isBiometricUnlockEnabled()) {
            clearBiometricSessionUnlocked();
            setLocked(shouldShowBiometricUnlockGate());
          } else if (isActive) {
            reevaluate();
          }
        });
        removeAppListener = () => {
          void handle.remove();
        };
      } catch {
        // App plugin unavailable — cold-start gate still works.
      }
    })();

    return () => {
      removeAppListener?.();
    };
  }, [isAuthenticated, reevaluate]);

  useEffect(() => {
    if (!locked || !isAuthenticated) return;
    void attemptUnlock();
  }, [locked, isAuthenticated, attemptUnlock]);

  const handleUsePassword = async () => {
    setBusy(true);
    try {
      clearBiometricSessionUnlocked();
      await logout();
      navigate('/login');
    } finally {
      setBusy(false);
    }
  };

  if (!isNativeShell() || !isAuthenticated || !locked) {
    return null;
  }

  return (
    <Box
      role="alertdialog"
      aria-labelledby="biometric-unlock-title"
      aria-describedby="biometric-unlock-body"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: (theme) => theme.zIndex.modal + 20,
        bgcolor: '#0d1320',
        color: '#e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Box
        sx={{
          maxWidth: 420,
          width: '100%',
          textAlign: 'center',
          bgcolor: '#111827',
          border: '1px solid #374151',
          borderRadius: 2,
          p: 3,
        }}
      >
        <FingerprintIcon sx={{ fontSize: 48, color: '#86efac', mb: 1.5 }} />
        <Typography id="biometric-unlock-title" variant="h6" sx={{ color: '#d1fae5', mb: 1 }}>
          Unlock Qixit Mail
        </Typography>
        <Typography id="biometric-unlock-body" variant="body2" sx={{ color: '#9ca3af', mb: 2 }}>
          Confirm with fingerprint or face unlock to reopen your mailbox on this device.
        </Typography>
        {error && (
          <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            onClick={() => void attemptUnlock()}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <FingerprintIcon />}
          >
            {busy ? 'Waiting…' : 'Unlock'}
          </Button>
          <Button variant="text" color="inherit" onClick={() => void handleUsePassword()} disabled={busy}>
            Use password instead
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default NativeBiometricUnlockOverlay;
